import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import BaseCard from "../../../base/BaseCard/BaseCard";
import BaseIcon from "../../../base/BaseIcon/BaseIcon";
import BaseLabel from "../../../base/BaseLabel";
import BaseInput from "../../../base/BaseInput/BaseInput";
import BaseButtonGroup from "../../../base/BaseButton/BaseButtonGroup";
import BaseButton from "../../../base/BaseButton/BaseButton";
import { TextRegular } from "../../../base/BaseText";

import PaymentIcon from "../../../images/icon-payment-method.svg";
import { ReactComponent as HelpIcon } from "../../../images/icon-help.svg";
import { ReactComponent as VisaIcon } from "../../../images/payment/icon-visa.svg";
import { ReactComponent as AmexSvgIcon } from "../../../images/payment/icon-amex.svg";
import { ReactComponent as MasterCardIcon } from "../../../images/payment/icon-master-card.svg";
import { showPaymentOption, submitPaymentDetails } from "../state/operators/paymentOperator";
import { isEmpty } from "../../../helpers/formatUtils";
import { REGEX, CARD, CLIENT } from "../../../helpers/constants";
import { INCIDENT_BATTERY_REPLACEMENT } from "../../../components/EnquiryOptions/Constants";
import { isValidExpiryDate } from "../../../helpers/dateUtils";
import { Translate, useTranslate } from "../../../helpers/languageUtils";
import BaseTooltip from "../../../base/BaseTooltip";

const SubmitCardDetails = () => {
  const { caseType } = useSelector((state) => state.journeyMessages);
  const t = useTranslate();
  const dispatch = useDispatch();

  const fee = useSelector(
    (state) => state.claim.payment.serviceFee.TotalAmount
  );
  const discountedfee = useSelector(
    (state) => state.claim.payment.serviceFee.TotalAmountWithDiscount
  );
  const client = useSelector((state) => state.app.client);
  const isSingtel = client === CLIENT.SINGTEL;

  const [disabled, setDisabled] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardType, setCardType] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cvvError, setCvvError] = useState("");
  const [cardNumberError, setCardNumberError] = useState("");
  const [expiryDateError, setExpiryDateError] = useState("");

  const handleClearClick = () => {
    setName("");
    setCardNumber("");
    setExpiryDate("");
    setCvv("");
  };

  const handleCvvChange = (input) => {
    if (!isNaN(input)) {
      setCvv(input);
      setCvvError("");
    }
  };

  const handleNameChange = (val) => {
    if (!val.match(REGEX.Text)) {
      setName(val);
      setNameError("");
    }
  };

  const handleCardNumberChange = (input) => {
    if (!isNaN(input)) {
      setCardNumber(input);
      if (input.match(REGEX.Visa)) {
        setCardType(CARD.VISA);
        setCardNumberError("");
      } else if (input.match(REGEX.Amex)) {
        setCardType(CARD.AMEX);
        setCardNumberError("");
      } else if (input.match(REGEX.MasterCard)) {
        setCardType(CARD.MASTER);
        setCardNumberError("");
      } else {
        setCardType("");
        if (input.length > 14) {
          setCardNumberError(t("Payment.Input.CardNumberError"));
        }
      }
    }
  };

  const handleCardType = () => {
    switch (cardType) {
      case CARD.VISA:
        return () => <VisaIcon className="mt-2" />;
      case CARD.AMEX:
        return () => <AmexSvgIcon />;
      case CARD.MASTER:
        return () => <MasterCardIcon />;
      default:
        return undefined;
    }
  };

  const handleExpiryDateChange = (value) => {
    if (!isNaN(value) || value[2] === "/") {
      const input = value.replace("/", "");
      setExpiryDate(
        input.length > 2
          ? `${input.substr(0, 2)}/${input.substr(2, input.length - 2)}`
          : input
      );
      if (input.length === 6) {
        const month = input.substr(0, 2);
        const year = input.substr(2, 4);
        const isValid = isValidExpiryDate(month, year);
        if (!isValid) {
          setExpiryDateError(t("Payment.Input.ExpiryDateError"));
        } else {
          setExpiryDateError("");
        }
      } else {
        setExpiryDateError("");
      }
    }
  };

  const shouldntSubmit =
    disabled ||
    isEmpty(name.trim()) ||
    isEmpty(cvv) ||
    isEmpty(expiryDate) ||
    isEmpty(cardType) ||
    isEmpty(cardNumber) ||
    !isEmpty(cardNumberError) ||
    !isEmpty(expiryDateError);

  const handleSubmitClick = () => {
    if (!anyErrors()) {
      const textPrint = t("SubmitButton");
      setDisabled(true);
      dispatch(
        submitPaymentDetails(
          name.trim(),
          cardNumber,
          expiryDate.split("/")[0],
          expiryDate.split("/")[1],
          cvv,
          cardType,
          textPrint
        )
      );
    }
  };

  const anyErrors = () => {
    let isError = false;
    if (name.trim().length < 3) {
      setNameError(t("Payment.Input.NameError"));
      isError = true;
    }
    if (name.trim().length >= 3) {
      let nameArray = name.trim().split(' ');
      let filteredNameArray = nameArray.filter(function (el) {
        return el != null;
      });

      if (filteredNameArray.length < 2) {
        setNameError(t("Payment.Input.NameError"));
        isError = true;
      }
    }
    if (cvv.length < 3) {
      setCvvError(t("Payment.Input.CvvError"));
      isError = true;
    }
    if (name.trim().length >= 3) {
      let nameArray = name.trim().split(' ');
      let filteredNameArray = nameArray.filter(function (el) {
        return el != null;
      });

      if (filteredNameArray.length < 2) {
        setNameError(t("Payment.Input.NameError"));
        isError = true;
      }
    }
    if (expiryDate.length !== 7) {
      setExpiryDateError(t("Payment.Input.ExpiryDateError"));
      isError = true;
    }
    if (
      !(
        (cardType === CARD.AMEX && cardNumber.length === 15) ||
        cardNumber.length === 16
      )
    ) {
      setCardNumberError(t("Payment.Input.CardNumberError"));
      isError = true;
    }
    return isError;
  };

  const HelpButton = () => (
    <BaseTooltip title="CVV code" text={t("Payment.Placeholder.CvvToolTip")}>
      <HelpIcon className="cursor-pointer" />
    </BaseTooltip>
  );

  const goBack = () => {
    setDisabled(true);
    dispatch(showPaymentOption(t("GoBackButton")));
  };

  const { ClientProductSkuNbr } = useSelector((state) => state.validation?.verifyAgreement?.VerifyAgreementsResults?.Agreement);
  const isSingtelStarter = isSingtel && (ClientProductSkuNbr && ClientProductSkuNbr.toLowerCase().indexOf("starter") > -1)

  return (
    <BaseCard>
      <BaseIcon icon={PaymentIcon} name="Payment Method" center />
      <BaseLabel className="my-2">
        <Translate i18nKey={isSingtelStarter 
          ? "Payment.TitleBatteryReplacement" 
          : caseType === INCIDENT_BATTERY_REPLACEMENT ? "Payment.TitleBatteryReplacement" : "Payment.Title"}>
          <TextRegular>
            Repair fee of ${{ fee: discountedfee || fee }} (inclusive of GST) is applicable.
          </TextRegular>
        </Translate>
      </BaseLabel>
      <BaseLabel className="my-2 mb-0">
        <Translate i18nKey="Payment.Label">
          <TextRegular>
            Please provide your
            <br /> Credit Card/Debit Card details below
          </TextRegular>
        </Translate>
      </BaseLabel>
      <BaseLabel className="my-0 mt-4 pl-2 text-left">
        <span className="font-extrabold">
          <Translate i18nKey="Payment.Input.Name" />
        </span>
      </BaseLabel>
      <BaseInput
        className="mb-3"
        placeholder={t("Payment.Placeholder.Name")}
        value={name}
        onChange={handleNameChange}
        disabled={disabled}
        medium
        errorText={nameError}
      />
      <BaseLabel className="my-0 mt-4 pl-2 text-left">
        <span className="font-extrabold">
          <Translate i18nKey="Payment.Input.CardNumber" />
        </span>
      </BaseLabel>
      <BaseInput
        className="mb-3"
        placeholder={t("Payment.Placeholder.CardNumber")}
        value={cardNumber}
        onChange={(val) => handleCardNumberChange(val.trim())}
        disabled={disabled}
        maxLength={16}
        medium
        errorText={cardNumberError}
        Suffix={handleCardType()}
      />
      <BaseLabel className="my-0 mt-4 pl-2 text-left">
        <span className="font-extrabold">
          <Translate i18nKey="Payment.Input.ExpiryDate" />
        </span>
      </BaseLabel>
      <BaseInput
        className="mb-3"
        placeholder={t("Payment.Placeholder.ExpiryDate")}
        value={expiryDate}
        onChange={(val) => handleExpiryDateChange(val.trim())}
        disabled={disabled}
        maxLength={7}
        errorText={expiryDateError}
        medium
      />
      <BaseLabel className="my-0 mt-4 pl-2 text-left">
        <span className="font-extrabold">
          <Translate i18nKey="Payment.Input.Cvv" />
        </span>
      </BaseLabel>
      <BaseInput
        type="password"
        placeholder={t("Payment.Placeholder.Cvv")}
        value={cvv}
        onChange={(val) => handleCvvChange(val.trim())}
        disabled={disabled}
        maxLength={4}
        medium
        Suffix={HelpButton}
        errorText={cvvError}
      />
      <BaseButtonGroup className="mt-6">
        <BaseButton
          text={t("ClearButton")}
          onClick={handleClearClick}
          disabled={disabled}
        />
        <BaseButton
          className="text-1.3"
          text={t("Payment.SubmitButton")}
          onClick={handleSubmitClick}
          disabled={shouldntSubmit}
          clicked={disabled}
        />
      </BaseButtonGroup>
      {!isSingtel && (
        <div
          className={` ${
            disabled
              ? "cursor-not-allowed items-center justify-around text-center m-4 "
              : "cursor-pointer items-center justify-around text-center m-4 "
          }`}
          disabled={disabled}
          onClick={!disabled && goBack}
        >
          <a
            className="text-srgreen-secondary text-xl"
          >
            {t("GoBackButton")}
          </a>
        </div>
      )}
    </BaseCard>
  );
};

export default SubmitCardDetails;
